// src/pages/AdminTagReport.tsx
// Admin: tarih aralığında tag başına sipariş raporu (associate hesaplarıyla çapraz doğrulama)
import React, { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import { Loader2, RefreshCw } from 'lucide-react'

type Preset = 'today' | 'd1' | 'd2' | 'd3' | 'd4' | 'd5' | 'd6' | 'week' | 'month' | 'custom'

interface TagRow {
  tagCode: string
  pool: string
  orderCount: number
  deliveredCount: number
  firstOrder: string
  lastOrder: string
}

interface TagReport {
  tags: TagRow[]
  totalOrders: number
  systemStart?: string
}

function toYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function computeRange(p: Preset, start: string, end: string): { s: string; e: string } {
  const now = new Date()
  const today = toYmd(now)
  const dayAgo = (n: number) => { const d = new Date(now); d.setDate(d.getDate() - n); return toYmd(d) }
  switch (p) {
    case 'today': return { s: today, e: today }
    case 'd1': return { s: dayAgo(1), e: dayAgo(1) } // dün (tek gün)
    case 'd2': return { s: dayAgo(2), e: dayAgo(2) }
    case 'd3': return { s: dayAgo(3), e: dayAgo(3) }
    case 'd4': return { s: dayAgo(4), e: dayAgo(4) }
    case 'd5': return { s: dayAgo(5), e: dayAgo(5) }
    case 'd6': return { s: dayAgo(6), e: dayAgo(6) }
    case 'week': return { s: dayAgo(6), e: today } // son 7 gün
    case 'month': { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { s: toYmd(d), e: today } }
    default: return { s: start, e: end }
  }
}

export default function AdminTagReport() {
  const [preset, setPreset] = useState<Preset>('week')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<TagReport | null>(null)
  const [live, setLive] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetchReport = useCallback(async (p: Preset, s: string, e: string, silent = false) => {
    const range = computeRange(p, s, e)
    if (!range.s || !range.e) return
    if (!silent) setLoading(true)
    try {
      const res = await apiService.getAdminTagReport(range.s, range.e)
      setData(res)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Tag report error', err)
      if (!silent) setData(null) // sessiz yenilemede hata olursa eldeki veriyi koru
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  // Preset değişince (custom hariç) otomatik getir
  useEffect(() => {
    if (preset !== 'custom') fetchReport(preset, start, end)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset])

  // Canlı güncelleme: açıkken her 15 sn'de bir sessizce yenile (tabloyu kırpmadan)
  useEffect(() => {
    if (!live) return
    if (preset === 'custom' && (!start || !end)) return
    const id = setInterval(() => fetchReport(preset, start, end, true), 15000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, preset, start, end])

  const presets: { key: Preset; label: string }[] = [
    { key: 'today', label: 'Bugün' },
    { key: 'd1', label: 'Dün' },
    { key: 'd2', label: '2 gün' },
    { key: 'd3', label: '3 gün' },
    { key: 'd4', label: '4 gün' },
    { key: 'd5', label: '5 gün' },
    { key: 'd6', label: '6 gün' },
    { key: 'week', label: '1 hafta' },
    { key: 'month', label: 'Bu Ay' },
    { key: 'custom', label: 'Özel' },
  ]

  // Aktif aralığı kullanıcıya göster (gün gün karşılaştırma için hangi tarih(ler)e baktığı belli olsun)
  const activeRange = computeRange(preset, start, end)
  const fmt = (ymd: string) => {
    if (!ymd) return '—'
    const [y, m, d] = ymd.split('-')
    return `${d}.${m}.${y}`
  }
  const activeLabel = !activeRange.s ? '' : activeRange.s === activeRange.e
    ? fmt(activeRange.s)
    : `${fmt(activeRange.s)} – ${fmt(activeRange.e)}`

  return (
    <div className="space-y-6">
      {/* Preset + tarih seçici */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          {presets.map(p => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                preset === p.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <input type="date" value={start} onChange={e => setStart(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <span className="text-gray-400">–</span>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <button onClick={() => fetchReport('custom', start, end)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">
                Getir
              </button>
            </div>
          )}
          <div className="ml-auto flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-gray-400">
                Son güncelleme: {lastUpdated.toLocaleTimeString('tr-TR')}
              </span>
            )}
            <button
              onClick={() => setLive(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                live ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}
              title={live ? 'Canlı güncelleme açık (15 sn)' : 'Canlı güncelleme kapalı'}
            >
              <span className={`w-2 h-2 rounded-full ${live ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              Canlı
            </button>
            <button onClick={() => fetchReport(preset, start, end)}
              className="p-2 text-gray-500 hover:text-blue-600" title="Yenile">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Aktif tarih + bilgi notu */}
      <div className="-mt-2 space-y-1">
        {activeLabel && (
          <p className="text-sm text-gray-700">
            Gösterilen (Amazon sipariş tarihi): <span className="font-semibold">{activeLabel}</span>
          </p>
        )}
        <p className="text-xs text-gray-500">
          Tarihler Amazon sipariş tarihine göredir (TR'ye çevrilmez) — associate hesabıyla aynı eksen.
          Yalnızca get-link'in verdiği gerçek tag'i taşıyan (doğru) siparişler gösterilir.
        </p>
      </div>

      {/* Özet kartlar */}
      {data && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm text-gray-500">Toplam Sipariş</p>
            <p className="text-2xl font-bold text-gray-800">{data.totalOrders}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm text-gray-500">Tag Sayısı</p>
            <p className="text-2xl font-bold text-gray-800">{data.tags.filter(t => t.tagCode !== '(etiketsiz)').length}</p>
          </div>
        </div>
      )}

      {/* Tablo */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : !data || data.tags.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Bu tarih aralığında veri yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Tag</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Havuz</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Sipariş</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Teslim</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">İlk Sipariş</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Son Sipariş</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.tags.map((t, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-gray-800">{t.tagCode}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      t.pool === 'bringgoship' ? 'bg-purple-100 text-purple-700'
                        : t.pool === 'genel' ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {t.pool}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-800">{t.orderCount}</td>
                  <td className="px-4 py-3 text-right text-green-600">{t.deliveredCount}</td>
                  <td className="px-4 py-3 text-left text-gray-500 whitespace-nowrap">{t.firstOrder}</td>
                  <td className="px-4 py-3 text-left text-gray-500 whitespace-nowrap">{t.lastOrder}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
